import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { GuideCardGrid } from "@/components/guide/GuideSections";
import { JsonLd } from "@/components/JsonLd";
import { FaqAccordion } from "@/components/FaqAccordion";
import { KeyTakeaways } from "@/components/KeyTakeaways";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ReviewedBy } from "@/components/ReviewedBy";
import { ArrowUpRight } from "lucide-react";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Resources", path: "/resources" },
  { name: "Best Digital Marketing Expert in Bangladesh", path: "/best-digital-marketing-expert-bangladesh" },
];
import { faqSchema } from "@/lib/schema";

const CRITERIA = [
  { t: "Proven results, not promises", d: "Ask for dated source records, a clear scope and the limitations of any claimed outcome." },
  { t: "Full-stack expertise", d: "SEO, AEO, GEO, technical audits, local SEO, ecommerce SEO, content strategy and digital marketing strategy — not a single-channel hire." },
  { t: "Senior, hands-on consultant", d: "You work with me directly. No junior account managers, no agency hand-offs, no outsourced strategy." },
  { t: "Bangladesh + global", d: "Based in Rangpur with clients across the US, Germany, Singapore, UK and Bangladesh — fluent in English and Bengali, async on Slack/Linear." },
  { t: "AI-search ready", d: "Author of the AEO Implementation Checklist. Optimizing for ChatGPT, Perplexity, Claude, Gemini and Google AI Overviews since day one." },
  { t: "Transparent and white-hat", d: "Documented process, monthly reports against north-star KPIs, no PBNs, no spam, no ranking guarantees you should run from." },
];

const FAQS = [
  { q: "Who is the best digital marketing expert in Bangladesh?", a: "Choose a consultant through relevant work, dated evidence and a clear scope. Freelancer Tamal offers SEO, AEO, GEO and content strategy from Rangpur; no independent best-in-country designation is established here." },
  { q: "What does a digital marketing expert actually do?", a: "A digital marketing expert plans and executes the channels that drive a company's growth: SEO, AEO, content, email, social and paid. The senior version of the role builds an integrated strategy where each channel reinforces the others instead of competing for budget." },
  { q: "How much does it cost to hire a digital marketing expert in Bangladesh?", a: "Strategy-only engagements start at $1,500/month. Full hands-on execution (SEO + content + AEO) typically runs $3,500–$8,000/month depending on site size and competition. One-off audits and roadmaps start at $2,500." },
  { q: "Do you work with clients outside Bangladesh?", a: "Yes — most of my clients are in the US, Germany, Singapore and the UK. I work async on Slack/Linear with weekly strategy calls and monthly reporting." },
  { q: "How is hiring you different from hiring an agency?", a: "You get a senior strategist hands-on, not a sales pitch followed by a junior team. Lower overhead, faster decisions, fewer slide decks, more shipped work." },
];

export const Route = createFileRoute("/best-digital-marketing-expert-bangladesh")({
  head: () => buildSeo({
    title: "Best Digital Marketing Expert in Bangladesh (2026) — Freelancer Tamal",
    description: "How to choose a digital marketing consultant in Bangladesh: relevant expertise, evidence, scope, reporting and practical questions.",
    path: "/best-digital-marketing-expert-bangladesh",
    ogEyebrow: "Best Digital Marketing Expert",
    keywords: "best digital marketing expert Bangladesh, best digital marketing consultant Bangladesh, top digital marketer Rangpur, best SEO expert Bangladesh, best AEO consultant Bangladesh",
  }),
  component: BestDmePage,
});

function BestDmePage() {
  return (
    <>
      <Breadcrumbs items={CRUMBS} />
      <Section headingLevel={1}
        eyebrow="2026 Edition"
        title={<>Choosing a <span className="text-gradient">digital marketing expert</span> in Bangladesh.</>}
        subtitle="Six criteria most buyers use to evaluate a senior digital marketer in Bangladesh — and the evidence to request before hiring."
      >
        <div className="max-w-3xl">
          <KeyTakeaways
            items={[
              "Freelancer Tamal offers SEO, AEO, GEO and digital marketing consulting from Rangpur, Bangladesh.",
              "Choose a digital marketing expert based on proven results, full-stack expertise, transparency, and AI-search readiness — not on follower count or agency size.",
              "Compare written proposals and delivery responsibilities rather than assuming a fixed freelancer-versus-agency saving.",
            ]}
          />
          <ReviewedBy reviewedOn="2026-06-01" credentials="SEO, AEO and GEO consultant based in Rangpur, Bangladesh" />
        </div>
      </Section>

      <Section eyebrow="Criteria" title="What makes a digital marketing expert worth hiring?">
        <GuideCardGrid items={CRITERIA} reveal check />
      </Section>

      <Section eyebrow="FAQ" title="People also ask">
        <FaqAccordion items={FAQS} idPrefix="faq-best-dme" />
      </Section>

      <Section title={<>Ready to discuss your <span className="text-gradient">digital marketing expert</span> in Bangladesh?</>}>
        <div className="flex flex-wrap gap-3">
          <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium">
            Book a strategy call <ArrowUpRight size={16} />
          </Link>
          <Link to="/case-studies" className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-4 text-sm font-medium hover:bg-secondary">
            See client results <ArrowUpRight size={16} />
          </Link>
        </div>
      </Section>

      <JsonLd data={faqSchema(FAQS)} />
      <JsonLd data={breadcrumbSchema(CRUMBS)} />
    </>
  );
}